/**
 * Narrowing guards for `FilterGroup.expressions: FilterGroupLeaf[]`.
 *
 * `types/filters.d.ts` is an ambient declaration file (no runtime output), so
 * the guards for the `FilterGroupLeaf` discriminated union it declares live
 * here instead — the canonical, shared runtime home for filter helpers
 * (barrel-exported via `@center-platform/spheres/filters`). Every consumer of
 * `FilterGroup.expressions` must narrow with these before reading
 * leaf-specific fields; never assume a leaf is a plain `FilterExpression`.
 */
import {
  ArcFilter,
  ArcFilterLeaf,
  FilterExpression,
  FilterGroup,
  FilterGroupLeaf,
  PerspectiveFilter,
  PerspectiveFilterLeaf,
  RelationshipFilter,
  RelationshipFilterLeaf,
  UnifiedFilters,
} from 'types/filters';

/** A plain property/arc-calculated expression — the unwrapped union member. */
export function isPropertyLeaf(leaf: FilterGroupLeaf): leaf is FilterExpression {
  return !('kind' in leaf);
}

/** A lane condition (perspective / relationship / arc) wrapped with its `kind` discriminant. */
export function isLaneLeaf(
  leaf: FilterGroupLeaf
): leaf is PerspectiveFilterLeaf | RelationshipFilterLeaf | ArcFilterLeaf {
  return 'kind' in leaf;
}

export function isPerspectiveLeaf(leaf: FilterGroupLeaf): leaf is PerspectiveFilterLeaf {
  return 'kind' in leaf && leaf.kind === 'perspective';
}

export function isRelationshipLeaf(leaf: FilterGroupLeaf): leaf is RelationshipFilterLeaf {
  return 'kind' in leaf && leaf.kind === 'relationship';
}

export function isArcLeaf(leaf: FilterGroupLeaf): leaf is ArcFilterLeaf {
  return 'kind' in leaf && leaf.kind === 'arc';
}

/**
 * A "measure" condition is not a new leaf kind — it is a `RelationshipFilter`
 * on the `measures_into` edge (see specs/unified-filters-v2.md "The measure
 * lane — a presentation rule, not a type"). Enrollment in an aggregate is
 * defined by `resolveRoster.ts` as an active outbound `measures_into` edge;
 * this predicate names that same relationship shape so catalog/UI code can
 * recognise a measure row without inventing a second evaluation surface.
 */
export function isMeasureRelationship(leaf: FilterGroupLeaf): leaf is RelationshipFilterLeaf {
  return isRelationshipLeaf(leaf) && leaf.filter.relationshipSystemName === 'measures_into';
}

/**
 * Property NAMES that are actually lane pseudo-properties. A canonical filter
 * expresses these as a `{kind}`-discriminated `FilterGroupLeaf` or a top-level
 * lane array — NEVER as a plain `{ property:'arc'|'perspective'|'relationship' }`
 * row. A raw API caller can still hand-craft the property-shaped form (bypassing
 * the UI/serialization), where `evaluateProperty` would read `undefined <op> value`
 * → often `true` → silently widen the filter. Every evaluation authority (the
 * async engine and the applicability validator) must fail closed on these names.
 */
export const LANE_BACKED_PROPERTY_KEYS: ReadonlySet<string> = new Set<string>([
  'arc',
  'perspective',
  'relationship',
]);

/** True when a raw property name is a lane pseudo-property that must fail closed. */
export function isLaneBackedPropertyName(property: string | undefined): boolean {
  return property !== undefined && LANE_BACKED_PROPERTY_KEYS.has(property);
}

/**
 * Flattens a `UnifiedFilters`' four lane arrays into one ordered
 * `FilterGroupLeaf[]` — property rows first, then perspective, relationship
 * and arc conditions wrapped with their `kind` discriminant.
 *
 * This is the bridge every catalog-first authoring surface needs: the editor
 * state is a `FilterGroup` (one tree, one row list, lanes and properties side
 * by side), while the persisted/composed shape is still `UnifiedFilters` with
 * a separate array per lane. Each lane filter is wrapped VERBATIM, so its own
 * `id` keeps keying the editor row across a round trip.
 *
 * Nested `filterGroups` are NOT flattened — they are groups, not leaves; a
 * caller that needs them carries them on the `FilterGroup.groups` side.
 */
export function unifiedLanesToLeaves(filters: UnifiedFilters | undefined): FilterGroupLeaf[] {
  if (!filters) return [];
  return [
    ...(filters.propertyFilters ?? []),
    ...(filters.perspectiveFilters ?? []).map<PerspectiveFilterLeaf>((filter) => ({ kind: 'perspective', filter })),
    ...(filters.relationshipFilters ?? []).map<RelationshipFilterLeaf>((filter) => ({ kind: 'relationship', filter })),
    ...(filters.arcFilters ?? []).map<ArcFilterLeaf>((filter) => ({ kind: 'arc', filter })),
  ];
}

/**
 * The inverse of `unifiedLanesToLeaves` — splits an editor's flat leaf list
 * back into the canonical per-lane arrays. A lane no leaf mentions is OMITTED
 * rather than emitted empty, so a round trip through the editor never widens
 * a stored filter with empty arrays it did not previously carry.
 */
export function leavesToUnifiedLanes(leaves: readonly FilterGroupLeaf[]): UnifiedFilters {
  const propertyFilters: FilterExpression[] = [];
  const perspectiveFilters: PerspectiveFilter[] = [];
  const relationshipFilters: RelationshipFilter[] = [];
  const arcFilters: ArcFilter[] = [];

  leaves.forEach((leaf) => {
    if (isPerspectiveLeaf(leaf)) perspectiveFilters.push(leaf.filter);
    else if (isRelationshipLeaf(leaf)) relationshipFilters.push(leaf.filter);
    else if (isArcLeaf(leaf)) arcFilters.push(leaf.filter);
    else propertyFilters.push(leaf);
  });

  const result: UnifiedFilters = {};
  if (propertyFilters.length > 0) result.propertyFilters = propertyFilters;
  if (perspectiveFilters.length > 0) result.perspectiveFilters = perspectiveFilters;
  if (relationshipFilters.length > 0) result.relationshipFilters = relationshipFilters;
  if (arcFilters.length > 0) result.arcFilters = arcFilters;
  return result;
}

/**
 * Every perspective-lane condition anywhere in a `FilterGroup` tree (top-level `expressions`
 * plus every nested `groups`), depth-first. A caller narrowing an eligible-schema catalog by
 * "which perspectives does this tree condition on" — the live replacement for the retired
 * `PerspectiveFilterState[]`/`includeInHierarchy` shape once a surface authors perspective
 * conditions inside the canonical tree instead (EAINT-877) — reads this rather than a
 * once-on-read snapshot that never updates as the tree is edited.
 */
export function collectPerspectiveFiltersFromTree(tree: FilterGroup | undefined): PerspectiveFilter[] {
  if (!tree) return [];
  const out: PerspectiveFilter[] = [];
  const walk = (group: FilterGroup) => {
    group.expressions.forEach((leaf) => {
      if (isPerspectiveLeaf(leaf)) out.push(leaf.filter);
    });
    (group.groups ?? []).forEach(walk);
  };
  walk(tree);
  return out;
}
